import { createInterval, Interval } from "./Interval";

export type AccountRole = "customer" | "supplier";
export type AccountAttachmentReviewStatus = "pending_approval" | "approved" | "rejected";
export type AccountAttachmentEffectiveStatus = AccountAttachmentReviewStatus | "missing" | "missing_expiry" | "expired";
export type AccountAttachmentRequirementKind = "upload" | "form";

export interface AccountAttachmentRequirement {
    id: string;
    tenant: string;
    name: string;
    description?: string;
    kind?: AccountAttachmentRequirementKind;
    declarations?: string[];
    requires_expiry: boolean;
    required_for_hours: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface AccountAttachment {
    id: string;
    account_id: string;
    requirement_id: string;
    name: string;
    content_type: "application/pdf" | "image/png" | "image/jpeg";
    content?: string;
    document_id?: string;
    content_url?: string;
    size?: number;
    sha256?: string;
    expires_at?: string;
    review_status: AccountAttachmentReviewStatus;
    review_note?: string;
    reviewed_at?: string;
    reviewed_by?: string;
    created_at: string;
    updated_at: string;
}

export interface AccountAttachmentRequirementStatus {
    requirement: AccountAttachmentRequirement;
    status: AccountAttachmentEffectiveStatus;
    attachment?: AccountAttachment;
}

export interface AccountAttachmentCompliance {
    compliant: boolean;
    requirements: AccountAttachmentRequirementStatus[];
}

export interface AccountAttachmentFormContext {
    requirement: AccountAttachmentRequirement;
    company: {
        kvk_number: string;
        registered_name: string;
        trade_names?: string[];
        address: AccountAddress;
        representative: string;
    };
    valid_from: string;
    valid_until: string;
}

export interface AccountAddress {
    id?: string;
    label: string;
    street: string;
    number: string;
    addition: string;
    postal_code: string;
    city: string;
    region: string;
    country: string;
    primary: boolean;
}

export interface AccountBankAccount {
    id?: string;
    label: string;
    iban: string;
    bic: string;
    account_name: string;
    primary: boolean;
}

export interface AccountPersonLink {
    person_id: string;
    roles: string[];
    primary: boolean;
}

export interface AccountBillingSettings {
    default_deadline: number;
    default_deadline_type: "fixed" | "pay_when_paid";
    invoice_interval: Interval;
    invoice_email: string;
    currency: string;
    default_vat_reverse_charged: boolean;
}

export type PlatformFeeType = "percentage" | "fixed";

export interface AccountWorkerRates {
    hourly_rate: number;
    platform_fee_type: PlatformFeeType;
    platform_fee_value: number;
}

export class Account {
    id = "";
    tenant = "";
    legal_name = "";
    trade_names: string[] = [];
    roles: AccountRole[] = [];
    addresses: AccountAddress[] = [];
    bank_accounts: AccountBankAccount[] = [];
    persons: AccountPersonLink[] = [];
    representative_person_id?: string;
    email = "";
    phone = "";
    vat_number = "";
    chamber_of_commerce_number = "";
    billing: AccountBillingSettings = {
        default_deadline: 14,
        default_deadline_type: "fixed",
        invoice_interval: createInterval(),
        invoice_email: "",
        currency: "EUR",
        default_vat_reverse_charged: false,
    };
    worker_rates: AccountWorkerRates = {
        hourly_rate: 0,
        platform_fee_type: "percentage",
        platform_fee_value: 0,
    };
    attachment_count = 0;
    pending_attachment_count = 0;
    approved_attachment_count = 0;
    created_at = "";
    updated_at = "";
}
